import { useEffect, useState } from "react";
import {
  Box, Button, Flex, HStack, Stack, VStack, SimpleGrid, Text, Input, Spinner, Switch, Badge, Select,
  Table, Thead, Tbody, Tr, Th, Td, Alert, AlertIcon, useToast, IconButton, Tooltip,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  useBreakpointValue,
} from "@chakra-ui/react";
import { FiPlus, FiSave, FiTrash2 } from "react-icons/fi";
import {
  listarFornecedoresCotacao,
  criarFornecedorCotacao,
  atualizarFornecedorCotacao,
  excluirFornecedorCotacao,
} from "../../services/api";

const mensagemErro = (r, padrao) =>
  r?.errors ? Object.values(r.errors).flat().join(" ") : (r?.message || padrao);

// A tabela só aparece a partir daqui; abaixo disso vira cartão.
const BP_TABELA = "xl";

// size responsivo direto no Modal não serve: o size "full" zera my/borderRadius e
// crava minH 100vh, e os breakpoints maiores só trocam o maxW — o resto vazaria
// para o desktop. Resolvendo o breakpoint aqui, só um size fica ativo por vez.
const TAMANHOS_MODAL = { base: "full", md: "2xl", [BP_TABELA]: "6xl" };

const Campo = ({ label, children }) => (
  <Box>
    <Text fontSize="11px" color="gray.500" fontWeight="semibold" mb={1}>{label}</Text>
    {children}
  </Box>
);

export default function ConfiguracoesFornecedoresCotacao({ isOpen, onClose }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [fornecedores, setFornecedores] = useState([]);
  const [salvando, setSalvando] = useState({});
  const [novo, setNovo] = useState({ nome: "", whatsapp: "" });
  const [criando, setCriando] = useState(false);
  const tamanhoModal = useBreakpointValue(TAMANHOS_MODAL, { ssr: false }) || "full";

  const carregar = async () => {
    setLoading(true);
    const data = await listarFornecedoresCotacao();
    setFornecedores(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  useEffect(() => {
    if (isOpen) carregar();
  }, [isOpen]);

  const atualizarLocal = (id, campo, valor) => {
    setFornecedores((arr) => arr.map((f) => (f.id === id ? { ...f, [campo]: valor } : f)));
  };

  const numeroOuNulo = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

  const salvar = async (forn) => {
    setSalvando((s) => ({ ...s, [forn.id]: true }));
    const r = await atualizarFornecedorCotacao(forn.id, {
      nome: forn.nome,
      whatsapp: forn.whatsapp,
      ativo: !!forn.ativo,
      frete_valor: numeroOuNulo(forn.frete_valor) ?? 0,
      frete_gratis_acima: numeroOuNulo(forn.frete_gratis_acima),
      prazo_entrega_dias: numeroOuNulo(forn.prazo_entrega_dias),
      prioridade: numeroOuNulo(forn.prioridade) ?? 3,
    });
    setSalvando((s) => ({ ...s, [forn.id]: false }));
    if (r && r.id) {
      toast({ title: `${r.nome} salvo`, status: "success", duration: 2000 });
    } else {
      toast({ title: mensagemErro(r, "Erro ao salvar fornecedor"), status: "error", duration: 4000 });
    }
  };

  const adicionar = async () => {
    if (!novo.nome.trim()) {
      toast({ title: "Informe o nome do fornecedor", status: "warning", duration: 2500 });
      return;
    }
    if (!/^\d{10,11}$/.test(novo.whatsapp)) {
      toast({ title: "WhatsApp deve ter DDD + número (10 ou 11 dígitos, sem o 55)", status: "warning", duration: 3500 });
      return;
    }
    setCriando(true);
    const r = await criarFornecedorCotacao({ nome: novo.nome.trim(), whatsapp: novo.whatsapp, ativo: true });
    setCriando(false);
    if (r && r.id) {
      setNovo({ nome: "", whatsapp: "" });
      setFornecedores((arr) => [...arr, r].sort((a, b) => a.nome.localeCompare(b.nome)));
      toast({ title: `${r.nome} adicionado`, status: "success", duration: 2000 });
    } else {
      toast({ title: mensagemErro(r, "Erro ao adicionar fornecedor"), status: "error", duration: 4000 });
    }
  };

  const excluir = async (forn) => {
    if (!window.confirm(`Excluir ${forn.nome} da lista de cotação?`)) return;
    setSalvando((s) => ({ ...s, [forn.id]: true }));
    const r = await excluirFornecedorCotacao(forn.id);
    setSalvando((s) => ({ ...s, [forn.id]: false }));
    if (r && r.success) {
      setFornecedores((arr) => arr.filter((f) => f.id !== forn.id));
      toast({ title: `${forn.nome} excluído`, status: "success", duration: 2000 });
    } else {
      toast({ title: mensagemErro(r, "Erro ao excluir fornecedor"), status: "error", duration: 4000 });
    }
  };

  const ativos = fornecedores.filter((f) => f.ativo).length;

  const opcoesPrioridade = (
    <>
      <option value={1}>1 (máxima)</option>
      <option value={2}>2</option>
      <option value={3}>3</option>
      <option value={4}>4</option>
      <option value={5}>5 (mínima)</option>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={tamanhoModal}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="#01695E" fontSize={{ base: "md", md: "lg" }} pr={12}>
          Fornecedores da cotação automática
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={{ base: 3, md: 6 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={10}>
              <Spinner size="xl" color="#F68633" thickness="4px" />
            </Box>
          ) : (
            <VStack align="stretch" spacing={4}>
              <Stack
                direction={{ base: "column", md: "row" }}
                justify="space-between"
                align={{ base: "flex-start", md: "center" }}
                spacing={2}
              >
                <Text fontSize="sm" color="gray.600" maxW="620px">
                  Todo fornecedor ativo recebe no WhatsApp o link de cada cotação gerada.
                  O frete e a prioridade são usados para montar os pedidos na tela de Cobertura de Furos
                  (prioridade 1 = a maior, usada para desempate quando o custo fica parecido).
                </Text>
                <Badge colorScheme="orange" fontSize="md" px={3} py={1} flexShrink={0}>
                  {ativos} ativo{ativos === 1 ? "" : "s"}
                </Badge>
              </Stack>

              {ativos === 0 && (
                <Alert status="warning" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  Nenhum fornecedor ativo — as cotações não serão enviadas para ninguém.
                </Alert>
              )}

              {/* Desktop largo: tabela. */}
              <Box display={{ base: "none", [BP_TABELA]: "block" }} overflowX="auto">
                <Table size="sm" variant="simple" sx={{ "th, td": { px: 2 } }}>
                  <Thead bg="gray.50">
                    <Tr>
                      <Th minW="170px">Nome</Th>
                      <Th minW="140px">WhatsApp</Th>
                      <Th w="110px">Frete</Th>
                      <Th w="130px">Frete grátis acima de</Th>
                      <Th w="90px">Prazo</Th>
                      <Th w="100px">Prioridade</Th>
                      <Th w="60px">Ativo</Th>
                      <Th w="120px"></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {fornecedores.map((f) => (
                      <Tr key={f.id} _hover={{ bg: "gray.50" }}>
                        <Td>
                          <Input
                            size="sm"
                            value={f.nome}
                            onChange={(e) => atualizarLocal(f.id, "nome", e.target.value)}
                          />
                        </Td>
                        <Td>
                          <Input
                            size="sm"
                            inputMode="numeric"
                            placeholder="11941524136"
                            value={f.whatsapp}
                            onChange={(e) => atualizarLocal(f.id, "whatsapp", e.target.value.replace(/\D/g, ""))}
                          />
                        </Td>
                        <Td>
                          <Input
                            size="sm"
                            type="number"
                            step="0.01"
                            min={0}
                            placeholder="0,00"
                            value={f.frete_valor ?? ""}
                            onChange={(e) => atualizarLocal(f.id, "frete_valor", e.target.value)}
                          />
                        </Td>
                        <Td>
                          <Input
                            size="sm"
                            type="number"
                            step="0.01"
                            min={0}
                            placeholder="sem isenção"
                            value={f.frete_gratis_acima ?? ""}
                            onChange={(e) => atualizarLocal(f.id, "frete_gratis_acima", e.target.value)}
                          />
                        </Td>
                        <Td>
                          <Input
                            size="sm"
                            type="number"
                            min={0}
                            placeholder="dias"
                            value={f.prazo_entrega_dias ?? ""}
                            onChange={(e) => atualizarLocal(f.id, "prazo_entrega_dias", e.target.value)}
                          />
                        </Td>
                        <Td>
                          <Select
                            size="sm"
                            value={f.prioridade ?? 3}
                            onChange={(e) => atualizarLocal(f.id, "prioridade", e.target.value)}
                          >
                            {opcoesPrioridade}
                          </Select>
                        </Td>
                        <Td>
                          <Switch
                            colorScheme="green"
                            isChecked={!!f.ativo}
                            onChange={(e) => atualizarLocal(f.id, "ativo", e.target.checked)}
                          />
                        </Td>
                        <Td>
                          <HStack spacing={1}>
                            <Button
                              size="sm"
                              leftIcon={<FiSave />}
                              colorScheme="green"
                              bg="#01695E"
                              onClick={() => salvar(f)}
                              isLoading={!!salvando[f.id]}
                            >
                              Salvar
                            </Button>
                            <Tooltip label="Excluir fornecedor" hasArrow>
                              <IconButton
                                aria-label={`Excluir ${f.nome}`}
                                size="sm"
                                variant="ghost"
                                colorScheme="red"
                                icon={<FiTrash2 />}
                                onClick={() => excluir(f)}
                                isDisabled={!!salvando[f.id]}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {/* Telas estreitas: cartões. Na tabela os 8 campos por linha saem da tela. */}
              <Stack display={{ base: "flex", [BP_TABELA]: "none" }} spacing={3}>
                {fornecedores.map((f) => (
                  <Box key={f.id} borderWidth="1px" borderRadius="md" p={3} bg="white">
                    <Flex align="center" gap={3} mb={3}>
                      <Input
                        size="sm"
                        fontWeight="semibold"
                        placeholder="Nome"
                        value={f.nome}
                        onChange={(e) => atualizarLocal(f.id, "nome", e.target.value)}
                      />
                      <HStack spacing={2} flexShrink={0}>
                        <Text fontSize="xs" color="gray.600">Ativo</Text>
                        <Switch
                          colorScheme="green"
                          isChecked={!!f.ativo}
                          onChange={(e) => atualizarLocal(f.id, "ativo", e.target.checked)}
                        />
                      </HStack>
                    </Flex>

                    <Campo label="WhatsApp">
                      <Input
                        size="sm"
                        inputMode="numeric"
                        placeholder="11941524136"
                        value={f.whatsapp}
                        onChange={(e) => atualizarLocal(f.id, "whatsapp", e.target.value.replace(/\D/g, ""))}
                      />
                    </Campo>

                    <SimpleGrid columns={{ base: 2, sm: 4 }} spacing={3} mt={3}>
                      <Campo label="Frete">
                        <Input
                          size="sm"
                          type="number"
                          step="0.01"
                          min={0}
                          placeholder="0,00"
                          value={f.frete_valor ?? ""}
                          onChange={(e) => atualizarLocal(f.id, "frete_valor", e.target.value)}
                        />
                      </Campo>
                      <Campo label="Frete grátis acima de">
                        <Input
                          size="sm"
                          type="number"
                          step="0.01"
                          min={0}
                          placeholder="sem isenção"
                          value={f.frete_gratis_acima ?? ""}
                          onChange={(e) => atualizarLocal(f.id, "frete_gratis_acima", e.target.value)}
                        />
                      </Campo>
                      <Campo label="Prazo (dias)">
                        <Input
                          size="sm"
                          type="number"
                          min={0}
                          placeholder="dias"
                          value={f.prazo_entrega_dias ?? ""}
                          onChange={(e) => atualizarLocal(f.id, "prazo_entrega_dias", e.target.value)}
                        />
                      </Campo>
                      <Campo label="Prioridade">
                        <Select
                          size="sm"
                          value={f.prioridade ?? 3}
                          onChange={(e) => atualizarLocal(f.id, "prioridade", e.target.value)}
                        >
                          {opcoesPrioridade}
                        </Select>
                      </Campo>
                    </SimpleGrid>

                    <HStack spacing={2} mt={3}>
                      <Button
                        flex="1"
                        size="sm"
                        leftIcon={<FiSave />}
                        colorScheme="green"
                        bg="#01695E"
                        onClick={() => salvar(f)}
                        isLoading={!!salvando[f.id]}
                      >
                        Salvar
                      </Button>
                      <IconButton
                        aria-label={`Excluir ${f.nome}`}
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        icon={<FiTrash2 />}
                        onClick={() => excluir(f)}
                        isDisabled={!!salvando[f.id]}
                      />
                    </HStack>
                  </Box>
                ))}
              </Stack>

              <Box borderTopWidth="1px" pt={4}>
                <Text fontWeight="bold" fontSize="sm" mb={2}>Adicionar fornecedor</Text>
                <Stack direction={{ base: "column", sm: "row" }} spacing={2}>
                  <Input
                    size="sm"
                    maxW={{ base: "100%", sm: "240px" }}
                    placeholder="Nome"
                    value={novo.nome}
                    onChange={(e) => setNovo({ ...novo, nome: e.target.value })}
                  />
                  <Input
                    size="sm"
                    inputMode="numeric"
                    maxW={{ base: "100%", sm: "180px" }}
                    placeholder="11941524136"
                    value={novo.whatsapp}
                    onChange={(e) => setNovo({ ...novo, whatsapp: e.target.value.replace(/\D/g, "") })}
                    onKeyDown={(e) => { if (e.key === "Enter") adicionar(); }}
                  />
                  <Button
                    size="sm"
                    leftIcon={<FiPlus />}
                    onClick={adicionar}
                    isLoading={criando}
                    flexShrink={0}
                  >
                    Adicionar
                  </Button>
                </Stack>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  WhatsApp com DDD + número, apenas dígitos e <b>sem o 55</b> (o sistema adiciona sozinho). Ex: 11941524136
                </Text>
              </Box>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} w={{ base: "100%", md: "auto" }}>Fechar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
